import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Third-parties.
import dayjs from 'dayjs';

import { Column, usePagination, useSortBy, useTable } from 'react-table';

import Skeleton from "react-loading-skeleton";

import TableCSVExport from '../../../component/TableCSVExport';

// Types.
import EventsDataTable from '../../../types/instance/tables/EventsDataTable';

// Components.
import RegisterEvent from '../../RegisterEvent';
import ReactTable from '../../ReactTable';

// Constants.
import {
    capitalizeString,
    getDefaultPageSize,
    prettifyChangesDetailsString,
    SEVERITY_LABEL,
} from "../../../helpers/utils";
import { TimeRangeContext } from "../../../context/TimeRangeContext";
import { InstanceContext } from "../../../context/InstanceContext";
import { DATA_LOADING, SEVERITY_CLASS_NAMES } from "../../Constants";
import InstanceEvents from "../InstanceEvents";
import ConditionalRender from "../../../helpers/ConditionalRender";
import SearchInput from '../../SearchInput';
import AnalysePeriod from "../../AnalyzePeriod";
import FilterButtonGroup from "../../FilterButtonGroup";
import { API_DATE_FORMAT } from "../../../helpers/timeRangeUtils";
import { availabilityGroupKey, useAvailabilityGroupLinks } from "../../../hooks/useAvailabilityGroupLinks";

// Sort rank for the Severity column; higher is worse. Non-alerts have no severity.
const SEVERITY_RANK: Record<number, number> = { 1: 4, 2: 3, 3: 2, 0: 1 };
function severityRank(row: EventsDataTable): number {
    return row.event === 'alert' ? (SEVERITY_RANK[row.severity] ?? 0) : -1;
}

function EventsTable(props: { loading: boolean | number, eventsDataTable: EventsDataTable[], hideRegister?: boolean }) {
    const inputRef = useRef<HTMLInputElement>(null);
    const { instances } = useContext(InstanceContext);
    const navigate = useNavigate();
    const location = useLocation();
    const isHostEvents = location.pathname.includes('/hosts/')

    const timeRangeContext = useContext(TimeRangeContext)
    const groupLinks = useAvailabilityGroupLinks(props.eventsDataTable);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus({ preventScroll: true });
        }
    }, []);

    const query = new URLSearchParams(location.search);
    const filterParam = query.get("filter");
    const eventTypeFromUrl = filterParam === "change" ? "changes" : filterParam === "custom" ? "events" : filterParam === "alert" ? "alerts" : "all";
    const [tableFilter, setTableFilter] = useState(query.get("search") || "");
    const [eventTypeFilter, setEventTypeFilter] = useState(eventTypeFromUrl);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const filter = params.get("filter");
        const eventType = filter === "change" ? "changes" : filter === "custom" ? "events" : filter === "alert" ? "alerts" : "all";
        setEventTypeFilter(eventType);
        setTableFilter(params.get("search") || "");
    }, [location.search]);
    const initialPageIndex = Number(query.get("page") || 0);
    const initialPageSize = Number(query.get("pageSize") || getDefaultPageSize());

    function clearTableFilter() {
        // Clear any pre-existing filter values.
        setTableFilter('');
        setEventTypeFilter('all');
    }

    const data = useMemo(() => {
        let filteredData = props.eventsDataTable;
        
        // Filter by event type
        if (eventTypeFilter === "changes") {
            filteredData = filteredData.filter(row => row.event === "change");
        } else if (eventTypeFilter === "alerts") {
            filteredData = filteredData.filter(row => row.event === "alert");
        } else if (eventTypeFilter === "events") {
            filteredData = filteredData.filter(row => row.event !== "change" && row.event !== "alert");
        }
        
        // Filter by search text
        filteredData = filteredData.filter(row => {
            return (
                row.instanceName.toLowerCase().includes(tableFilter.toLowerCase()) ||
                row.changeType.toLowerCase().includes(tableFilter.toLowerCase()) ||
                ((!row.details) ? '' : row.details).toLowerCase().includes(tableFilter.toLowerCase()) ||
                row.changeFrom?.toLowerCase().includes(tableFilter.toLowerCase()) ||
                (row.changeFrom === null && 'null'.includes(tableFilter.toLowerCase()) && row.changeType === 'Parameter Altered') ||
                (row.changeTo === null && 'null'.includes(tableFilter.toLowerCase()) && row.changeType === 'Parameter Altered') ||
                (row.timeslice?.toString().includes(tableFilter)) ||
                row.changeTo?.toLowerCase().includes(tableFilter.toLowerCase()) ||
                row.event?.toLowerCase().includes(tableFilter.toLowerCase()) ||
                row.startedEnded?.toLowerCase().includes(tableFilter.toLowerCase()) ||
                row.units?.toLowerCase().includes(tableFilter.toLowerCase()) ||
                row.threshold?.toLowerCase().includes(tableFilter.toLowerCase()) ||
                row.statistic?.toLowerCase().includes(tableFilter.toLowerCase()) ||
                row.title?.toLowerCase().includes(tableFilter.toLowerCase()) ||
                (row.decreasing?.toString() === 'true' && 'less than'.includes(tableFilter.toLowerCase())) ||
                (row.decreasing?.toString() === 'false' && 'greater than'.includes(tableFilter.toLowerCase())) ||
				row.name.includes(tableFilter.toLowerCase()) ||
				(row.event === 'alert' && (SEVERITY_LABEL[row.severity] ?? '').toLowerCase().includes(tableFilter.toLowerCase()))
            )
        })
        // TODO: temporary fix for oracle event type name, a different solution is needed here
        let instanceType = ''

        filteredData.forEach(row => {
            if (props.eventsDataTable) {
                instanceType = instances.find(instance => instance.id === row.instanceId)?.type || ''
            }
            if (instanceType === 'oracle') {
                row.changeType = row.changeType.replace('database', 'schema');
            }
        })
        return filteredData
    }, [props.eventsDataTable, tableFilter, eventTypeFilter]);

    const columns: Column[] = useMemo(() => [
        {
            accessor: 'id',
            disableSortBy: true
        },
        {
            accessor: 'timesliceEnd',
            disableSortBy: true
        },
        {
            accessor: 'color',
            disableSortBy: true
        },
        {
            accessor: 'htmlIconCode',
            disableSortBy: true
        },
        {
            accessor: 'instanceId',
            disableSortBy: true
        },
        {
            accessor: 'title',
            disableSortBy: true
        },
        {
            accessor: 'detailsUrl',
            disableSortBy: true
        },
        {
            accessor: 'changeFrom',
            disableSortBy: true
        },
        {
            accessor: 'changeTo',
            disableSortBy: true
        },
        {
            accessor: 'isHost',
            disableSortBy: true
        },
        {
            accessor: 'startedEnded',
            disableSortBy: true
        },
        {
            accessor: 'units',
            disableSortBy: true
        },
        {
            accessor: 'statistic',
            disableSortBy: true
        },
        {
            accessor: 'threshold',
            disableSortBy: true
        },
        {
            accessor: 'decreasing',
            disableSortBy: true
        },
        {
            accessor: 'name',
            disableSortBy: true
        },
        {
            Header: 'Time',
            accessor: 'timeslice',
            className: 'fw-bold',
            defaultCanSort: true,
            sortType: 'basic',
            Cell: ({ cell }) => {
                const { values, original } = cell.row;
                const originalData = original as EventsDataTable;
                const { timeslice, timesliceEnd, instanceName, isHost, instanceId } = values;
                const tz = timeRangeContext.timeRange.timezone;

                const formatTime = (time: string | Date) =>
                        dayjs.tz(time, tz).format("YYYY-MM-DD HH:mm");

                const baseTime = dayjs.tz(timeslice, tz);
                const fm = baseTime.subtract(30, "minute").format(API_DATE_FORMAT);
                const toParam = baseTime.add(30, "minute").format(API_DATE_FORMAT);
                const searchParams = `fm=${fm}&to=${toParam}&tz=${timeRangeContext.timeRange.timezone}&interval=${timeRangeContext.timeRange.interval}`;

                // If instance name is unknown, return plain text
                if (instanceName === "Unknown") {
                    return timesliceEnd
                            ? `${formatTime(timeslice)} - ${formatTime(timesliceEnd)}`
                            : formatTime(timeslice);
                }

                // Build target URL
                const fullUrl = isHost && originalData.host?.id 
                    ? `/hosts/${originalData.host.id}?${searchParams}`
                    : `/instances/${instanceId}/activity?${searchParams}`;

                return (
                        <a href={fullUrl}>
                            {formatTime(timeslice)}
                        </a>
                );
            },
        },
        {
            Header: 'Entity',
            accessor: 'instanceName',
            defaultCanSort: true,
            sortType: 'basic',
            Cell: ({ cell }) => {
                const { values, original } = cell.row;
                const originalData = original as EventsDataTable;
                const { isHost, instanceName, instanceId, timeslice } = values;

                if (instanceName === "Unknown") {
                    return <span>Unknown</span>;
                }

                const tz = timeRangeContext.timeRange.timezone;
                const baseTime = dayjs.tz(timeslice, tz);
                const fm = baseTime.subtract(30, "minute").format(API_DATE_FORMAT);
                const toParam = baseTime.add(30, "minute").format(API_DATE_FORMAT);
                const searchParams = `fm=${fm}&to=${toParam}&tz=${tz}&interval=${timeRangeContext.timeRange.interval}`;

                // Raised against an availability group, so open that group's screen.
                const groupPath = originalData.groupName
                        ? groupLinks.get(availabilityGroupKey(instanceId, originalData.groupName))
                        : undefined;
                if (groupPath) {
                    return <a href={`${groupPath}?${searchParams}`}>{instanceName}</a>;
                }

                return isHost ? (
                        <a href={`/hosts/${originalData.host?.id}?${searchParams}`}>
                            {instanceName}
                        </a>
                ) : (
                        <a href={`/instances/${instanceId}/activity?${searchParams}`}>
                            {instanceName}
                        </a>
                );
            },
        },
        {
            Header: 'Event',
            accessor: 'event',
            defaultCanSort: false,
            Cell: ({ cell }) => {
                return <span className="text-capitalize">{cell.row.values.event}</span>
            }
        },
        {
            Header: 'Type',
            accessor: 'changeType',
            defaultCanSort: true,
            sortType: 'basic',
            Cell: ({ cell }) => {

                return (
                    cell.row.values.changes !== null || cell.row.values.event === 'change' ? (
                        <React.Fragment>
                            <span className={`${cell.row.values.htmlIconCode !== '' ? 'with-icon' : ''} event-type `}>
                                    <div>
                                        {cell.row.values.event !== 'custom' ? capitalizeString(cell.row.values.changeType) : cell.row.values.changeType}
                                    </div>
                                {cell.row.values.htmlIconCode !== '' && (
                                    <i className={`${cell.row.values.htmlIconCode} ms-2`} aria-hidden="true"/>
                                )}
                            </span>
                        </React.Fragment>
                    ) : (
                        <Skeleton/>
                    )
                )
            }
        },
        {
            // An icon keeps the column as narrow as its cells. As a function, the
            // CSV export still labels it from the accessor.
            Header: () => <i className="fas fa-bell fa-fw" role="img" aria-label="Severity"
                             data-tooltip-id="global-tooltip" data-tooltip-content="Severity"/>,
            // Left aligned, not centred: the sort indicator sits beside the bell,
            // so centring would put the bell off the axis of the icons below it.
            accessor: 'severity',
            defaultCanSort: true,
            // Worst first when descending: Critical, Warning, Info, Normal, then non-alerts.
            sortType: (rowA: any, rowB: any) =>
                    severityRank(rowA.original) - severityRank(rowB.original),
            Cell: ({ cell }) => {
                const { event, severity } = cell.row.values;
                if (event !== 'alert') {
                    return null;
                }
                return <i className={`fal fa-exclamation-triangle fa-fw ${SEVERITY_CLASS_NAMES[severity] ?? ''}`}
                          data-tooltip-id="global-tooltip"
                          data-tooltip-content={SEVERITY_LABEL[severity] ?? 'Unknown'}
                          role="img" aria-label={SEVERITY_LABEL[severity] ?? 'Unknown'}/>
            }
        },
        {
            Header: 'Details',
            accessor: 'details',
            defaultCanSort: false,
            Cell: ({ cell }) => {
                return <InstanceEvents event={cell.row.values}/>
            }
        },
        ...(isHostEvents ? [] : [{
            Header: 'Analyse',
            accessor: 'analyse',
            disableSortBy: true,
            headerClassName: 'text-end',
            className: 'text-center',
            defaultCanSort: false,
            Cell: ({ cell }: any) => {
                return <AnalysePeriod cell={cell} timezone={timeRangeContext.timeRange.timezone}/>
            }
        }])
    ], [timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.timezone, props.eventsDataTable, groupLinks]);

    const tableInstance = useTable({
            columns,
            data,
            initialState: {
                hiddenColumns: ['id', 'timesliceEnd', 'color', 'instanceId', 'htmlIconCode', 'title', 'detailsUrl', 'changeFrom', 'changeTo', 'startedEnded', 'units', 'statistic', 'threshold', 'decreasing', 'isHost', 'name'],
                pageIndex: initialPageIndex,
                pageSize: initialPageSize,
                sortBy: [{ id: 'timeslice', desc: true }]
            },
            autoResetPage: false,
            autoResetSortBy: false,
            autoResetFilters: false,
            autoResetSelectedRows: false,
            autoResetExpanded: false,
            autoResetGroupBy: false,
        },
        useSortBy, usePagination);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize }
    } = tableInstance;

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        if (eventTypeFilter === "changes") {
            params.set('filter', 'change');
        } else if (eventTypeFilter === "events") {
            params.set('filter', 'custom');
        } else if (eventTypeFilter === "alerts") {
            params.set('filter', 'alert');
        } else {
            params.delete('filter');
        }

        if (tableFilter) {
            params.set('search', tableFilter);
        } else {
            params.delete('search');
        }

        params.set('page', pageIndex.toString());
        params.set('pageSize', pageSize.toString());

        navigate({ search: params.toString() }, { replace: true });
    }, [eventTypeFilter, tableFilter, pageIndex, pageSize]);

    useEffect(() => {
        if (tableFilter || eventTypeFilter !== "all") {
            gotoPage(0);
        }
    }, [tableFilter, eventTypeFilter]);

    // Transform function to format details field for CSV export
    const transformCsvData = useMemo(() => (csvData: any[]) => {
        return csvData.map((row: any) => {
            // Get original row data (stored in _original by TableCSVExport)
            const originalRow = row._original || row;
            
            // Initialize details if not present
            if (row.details === undefined || row.details === null) {
                row.details = '';
            }
            
            // Format details based on event type using original row data
            if (originalRow.event === 'alert') {
                row.details = `${originalRow.startedEnded || ''} ${originalRow.statistic || ''} ${originalRow.decreasing?.toString() === 'true' ? 'less than' : ' greater than'} ${originalRow.threshold || ''}${originalRow.units || ''}`;
            } else {
                // For non-alert events, use title if available, otherwise use details
                row.details = originalRow.title === '' ? (originalRow.details || '') : originalRow.title;
            }
            
            // Severity only applies to alerts.
            row.severity = originalRow.event === 'alert' ? (SEVERITY_LABEL[originalRow.severity] ?? '') : '';

            // Append change information if available
            if (originalRow.changeFrom && originalRow.changeTo) {
                row.details += `(from ${prettifyChangesDetailsString(originalRow.changeFrom + (originalRow.units ? originalRow.units : ''))} to ${prettifyChangesDetailsString(originalRow.changeTo + (originalRow.units ? originalRow.units : ''))})`;
            }
            
            return row;
        });
    }, []);

    useEffect(() => {
        
    }, [pageIndex])

    

    return (
        <React.Fragment>
            <div className="row table-search align-items-center">
                <div className="col-auto">
                    <FilterButtonGroup
                        options={[
                            { label: 'All', value: 'all', variant: 'primary' },
                            { label: 'Changes', value: 'changes', variant: 'primary', icon: 'fa-exchange-alt' },
                            { label: 'Events', value: 'events', variant: 'success', icon: 'fa-calendar-check' },
                            { label: 'Alerts', value: 'alerts', variant: 'danger', icon: 'fa-exclamation-circle' }
                        ]}
                        selectedValue={eventTypeFilter}
                        onChange={setEventTypeFilter}
                        ariaLabel="Event type filter"
                    />
                </div>
                <div className="col-auto ms-auto">
                    <ConditionalRender if={!props.hideRegister && !isHostEvents}>
                        <RegisterEvent timeRangeContext={timeRangeContext}/>
                    </ConditionalRender>
                    <TableCSVExport
                        tableInstance={tableInstance}
                        filename="Changes and Events"
                        transformData={transformCsvData}
                    />
                </div>
                <div className="col-md-2">
                    <SearchInput
                        ref={inputRef}
                        value={tableFilter}
                        onChange={(e) => setTableFilter(e.target.value)}
                        onClear={clearTableFilter}
                    />
                </div>
            </div>
            <ReactTable
                getTableProps={getTableProps}
                getTableBodyProps={getTableBodyProps}
                headerGroups={headerGroups}
                prepareRow={prepareRow}
                page={page}
                canPreviousPage={canPreviousPage}
                canNextPage={canNextPage}
                pageOptions={pageOptions}
                pageCount={pageCount}
                gotoPage={gotoPage}
                nextPage={nextPage}
                previousPage={previousPage}
                setPageSize={setPageSize}
                pageIndex={pageIndex}
                pageSize={pageSize}
                dataLength={data.length}
                error={null}
                loading={props.loading === DATA_LOADING || props.loading === true}
            />
        </React.Fragment>
    );
}

export default EventsTable;
